/**
 * API service for the ANNA AI Studio
 */

import {
  DocumentType,
  DocumentAnalysisResponse,
  ModelInfo,
  DocumentTypeConfig,
  PDFPreviewData,
} from "../types";
import { GenerationSection } from "../components/GenerateDraftSidebar";
import authService from "./auth";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Analyze a document. Only file and sessionId are required — the backend loads
 * default model, system prompt, and user prompt from the document-type config.
 * Optional overrides (modelId, systemPrompt, userPrompt) are forwarded when
 * provided (e.g. from the advanced-settings panel).
 */
export async function analyzeDocument(
  documentType: DocumentType,
  file: File | null,
  sessionId?: number | null,
  overrides?: {
    modelId?: string;
    systemPrompt?: string;
    userPrompt?: string;
  }
): Promise<DocumentAnalysisResponse> {
  const formData = new FormData();
  if (file) {
    formData.append("file", file);
  }
  if (sessionId) {
    formData.append("session_id", sessionId.toString());
  }
  if (overrides?.modelId) {
    formData.append("model_id", overrides.modelId);
  }
  if (overrides?.systemPrompt) {
    formData.append("system_prompt", overrides.systemPrompt);
  }
  if (overrides?.userPrompt) {
    formData.append("user_prompt", overrides.userPrompt);
  }

  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/analyze/${documentType}`,
    {
      method: "POST",
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to analyze document");
  }

  return response.json();
}

/**
 * Get available models
 */
export async function getAvailableModels(): Promise<ModelInfo[]> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/models`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch models");
  }

  return response.json();
}

/**
 * Get document type configuration (prompts and schema)
 */
export async function getDocumentTypeConfig(
  documentType: DocumentType
): Promise<DocumentTypeConfig> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/document-types/${documentType}/config`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(
      error.detail || "Failed to fetch document type configuration"
    );
  }

  return response.json();
}

/**
 * Generate a treatment plan section
 */
export async function generateTreatmentPlanSection(
  section: GenerationSection,
  modelId: string,
  systemPrompt: string,
  userPrompt: string,
  referenceData: Array<{ title: string; content: any }>,
  ragEnabled: boolean = false,
  ragNumChunks: number = 5,
  ragMinScore: number = 0.3,
  sessionId?: number | null
): Promise<any> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-plans/generate`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        section,
        model_id: modelId,
        system_prompt: systemPrompt,
        user_prompt: userPrompt,
        reference_data: referenceData,
        rag_enabled: ragEnabled,
        rag_num_chunks: ragNumChunks,
        rag_min_score: ragMinScore,
        session_id: sessionId || undefined,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to generate section");
  }

  return response.json();
}

/**
 * Get treatment plan section configuration
 */
export async function getSectionConfig(sectionId: string): Promise<any> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-plans/sections/${sectionId}/config`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch section configuration");
  }

  return response.json();
}

/**
 * Analyze VB-MAPP Excel file using dedicated parser.
 * 
 * Supports two modes:
 * - Mode 1 (new file): file is provided -> upload to S3, then analyze
 * - Mode 2 (re-analyze): no file but sessionId -> download from S3, then analyze
 */
export async function analyzeVBMAPP(
  file: File | null,
  sessionId?: number | null
): Promise<DocumentAnalysisResponse> {
  const formData = new FormData();
  if (file) {
    formData.append("file", file);
  }
  if (sessionId) {
    formData.append("session_id", sessionId.toString());
  }

  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/vb-mapp/analyze`,
    {
      method: "POST",
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to analyze VB-MAPP Excel");
  }

  return response.json();
}

/**
 * Get Excel file preview as PDF (converts Excel to PDF using LibreOffice)
 */
export async function getExcelPreview(file: File): Promise<PDFPreviewData> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/vb-mapp/preview`,
    {
      method: "POST",
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to generate Excel preview");
  }

  return response.json();
}

/**
 * Export treatment plan sections to Word or PDF document
 */
export async function exportTreatmentPlan(
  sections: Map<GenerationSection, any>,
  format: "word" | "pdf" = "word"
): Promise<Blob> {
  // Convert Map to plain object for JSON serialization
  const sectionsObj: Record<string, any> = {};
  sections.forEach((value, key) => {
    sectionsObj[key] = value;
  });

  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-plans/export`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        sections: sectionsObj,
        format: format,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to export treatment plan");
  }

  return response.blob();
}
